import React from 'react';
import { DEFAULT_COMPANY_INFO, resolveCompanyLogoUrl } from '../../../utils/companyInfo';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const raw = String(dateString).trim();
  const date = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T00:00:00`) : new Date(raw);
  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMoney = (value) => new Intl.NumberFormat('en-KE', {
  style: 'currency',
  currency: 'KES',
  minimumFractionDigits: 2
}).format(Number(value || 0));

const chunk = (items = [], size = 8) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const pages = [];
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }
  return pages;
};

const safeText = (value, fallback = '-') => {
  const raw = String(value ?? '').trim();
  return raw || fallback;
};

const getStatusLabel = (status) => safeText(String(status || '').replace(/_/g, ' ').toUpperCase(), 'UNKNOWN');

const getServiceLocationLabel = (value) => {
  if (String(value || '').toLowerCase() === 'in_house') return 'In-House';
  if (String(value || '').toLowerCase() === 'external') return 'External';
  return safeText(value, 'Unknown');
};

const pageShell = {
  width: '210mm',
  minHeight: '297mm',
  boxSizing: 'border-box',
  padding: '12mm',
  margin: '0 auto 8mm',
  backgroundColor: '#fff',
  color: '#111827',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
  overflow: 'hidden'
};

const sectionCard = {
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  background: '#ffffff',
  padding: '12px'
};

const badge = (background, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  minWidth: '58px',
  minHeight: '42px',
  borderRadius: '12px',
  background,
  color,
  fontSize: '10px',
  fontWeight: 800,
  padding: '6px 10px',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  lineHeight: 1.1,
  boxSizing: 'border-box'
});

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: '12px', fontWeight: 800, color: '#2a2e30', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '.04em' }}>
    {children}
  </div>
);

const KeyValue = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '11px', color: '#6b7280' }}>{label}</div>
    <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827', marginTop: '3px', lineHeight: 1.4 }}>
      {value}
    </div>
  </div>
);

const InfoGrid = ({ items = [] }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
    {items.map((item) => (
      <KeyValue key={item.label} label={item.label} value={item.value} />
    ))}
  </div>
);

const LineTable = ({ title, rows, columns, emptyText, footer }) => (
  <div style={{ marginBottom: '12px' }}>
    <SectionTitle>{title}</SectionTitle>
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <thead>
        <tr style={{ backgroundColor: '#EDE4DA' }}>
          {columns.map((column) => (
            <th
              key={column.key}
              style={{
                padding: '8px',
                border: '1px solid #d1d5db',
                fontSize: '11px',
                textAlign: column.align || 'left',
                width: column.width
              }}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} style={{ padding: '14px', border: '1px solid #d1d5db', textAlign: 'center', color: '#6b7280' }}>
              {emptyText}
            </td>
          </tr>
        ) : (
          rows.map((row, index) => (
            <tr key={`${title}-${index}`}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  style={{
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    fontSize: '11px',
                    textAlign: column.align || 'left',
                    verticalAlign: 'top',
                    lineHeight: 1.45,
                    wordBreak: 'break-word'
                  }}
                >
                  {column.render ? column.render(row, index) : safeText(row[column.key])}
                </td>
              ))}
            </tr>
          ))
        )}
        {footer}
      </tbody>
    </table>
  </div>
);

const StatusPill = ({ label, background, color }) => (
  <span style={badge(background, color)}>{label}</span>
);

const JobCardPrintSheet = ({ jobCard, companyInfo = DEFAULT_COMPANY_INFO, generatedBy = '' }) => {
  if (!jobCard) return null;

  const companyName = companyInfo?.company_name || DEFAULT_COMPANY_INFO.company_name;
  const companyAddress = companyInfo?.company_address || '';
  const companyPhone = companyInfo?.company_phone || '';
  const companyEmail = companyInfo?.company_email || '';
  const logoUrl = resolveCompanyLogoUrl(companyInfo);

  const parts = Array.isArray(jobCard.parts) ? jobCard.parts : [];
  const charges = Array.isArray(jobCard.charges) ? jobCard.charges : [];
  const partsTotal = parts.reduce((sum, row) => sum + Number(row.total_cost || 0), 0);
  const chargesTotal = charges.reduce((sum, row) => sum + Number(row.total_amount || 0), 0);
  const totalCost = Number(jobCard.total_cost ?? (partsTotal + chargesTotal + Number(jobCard.other_costs || 0) + Number(jobCard.labor_cost || 0) + Number(jobCard.external_mechanic_cost || 0)));
  const printerName = safeText(
    generatedBy
      || jobCard.generated_by_name
      || jobCard.edit_by_name
      || jobCard.input_by_name
      || 'Fleet Management'
  );

  const partsPages = chunk(parts, 7);
  const chargesPages = chunk(charges, 7);
  const finalPages = [];

  finalPages.push({
    key: 'overview',
    body: (
      <div style={pageShell}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', borderBottom: '2px solid #FF5733', paddingBottom: '12px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', minWidth: 0 }}>
              <img
                src={logoUrl}
                alt="Company Logo"
                crossOrigin="anonymous"
                style={{ width: '56px', height: '56px', objectFit: 'contain', flexShrink: 0 }}
              />
              <div style={{ minWidth: 0 }}>
                <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#2a2e30', lineHeight: 1.15 }}>
                  {companyName}
                </h1>
                <div style={{ marginTop: '4px', fontSize: '12px', color: '#FF5733', fontWeight: 700 }}>
                  Job Card Document
                </div>
                {companyAddress && (
                  <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#6b7280', lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                    {companyAddress}
                  </p>
                )}
                {(companyPhone || companyEmail) && (
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280', lineHeight: 1.4 }}>
                    {[companyPhone, companyEmail].filter(Boolean).join(' | ')}
                  </p>
                )}
              </div>
            </div>

            <div style={{ textAlign: 'right', minWidth: '188px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#6b7280' }}>
                Job Card #{safeText(jobCard.job_card_no || jobCard.id)}
              </div>
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>Opened</div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#2a2e30' }}>{formatDate(jobCard.opened_date)}</div>
              <div style={{ marginTop: '8px' }}>
                <StatusPill label={getStatusLabel(jobCard.status)} background="#eef2ff" color="#3730a3" />
              </div>
              <div style={{ marginTop: '8px', display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                {jobCard.locked ? <StatusPill label="Locked" background="#fef3c7" color="#92400e" /> : null}
                {jobCard.cancelled ? <StatusPill label="Cancelled" background="#fee2e2" color="#b91c1c" /> : null}
                {jobCard.invoiced ? <StatusPill label="Invoiced" background="#f3e8ff" color="#7c3aed" /> : null}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.06fr 0.94fr', gap: '12px', marginBottom: '12px' }}>
            <div style={{ ...sectionCard, background: '#fff8f5' }}>
              <SectionTitle>Vehicle & Ownership</SectionTitle>
              <InfoGrid
                items={[
                  { label: 'Plate Number', value: safeText(jobCard.plate_no) },
                  { label: 'Owner', value: safeText(jobCard.owner_name || jobCard.owner_code) },
                  { label: 'Owner Email', value: safeText(jobCard.owner_email) },
                  { label: 'Owner Phone', value: safeText(jobCard.owner_phone) },
                  { label: 'Driver ID', value: safeText(jobCard.driver_id) },
                  { label: 'Service Location', value: getServiceLocationLabel(jobCard.service_location) }
                ]}
              />
              <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                <KeyValue label="Vehicle" value={safeText([jobCard.make, jobCard.model, jobCard.year].filter(Boolean).join(' '))} />
                <KeyValue label="Mileage at Service" value={jobCard.mileage_at_service ? `${Number(jobCard.mileage_at_service).toLocaleString()} km` : '-'} />
                <KeyValue label="Service Center" value={safeText(jobCard.service_center_name)} />
              </div>
            </div>

            <div style={{ ...sectionCard, background: '#f9fafb' }}>
              <SectionTitle>Document Summary</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px 12px', fontSize: '12px' }}>
                <div style={{ color: '#6b7280' }}>Total Cost</div>
                <div style={{ fontWeight: 800, textAlign: 'right' }}>{formatMoney(totalCost)}</div>
                <div style={{ color: '#6b7280' }}>Parts Cost</div>
                <div style={{ fontWeight: 700, textAlign: 'right' }}>{formatMoney(jobCard.parts_cost || partsTotal)}</div>
                <div style={{ color: '#6b7280' }}>Labor Cost</div>
                <div style={{ fontWeight: 700, textAlign: 'right' }}>{formatMoney(jobCard.labor_cost || 0)}</div>
                <div style={{ color: '#6b7280' }}>Charges Cost</div>
                <div style={{ fontWeight: 700, textAlign: 'right' }}>{formatMoney(chargesTotal)}</div>
                <div style={{ color: '#6b7280' }}>Other Costs</div>
                <div style={{ fontWeight: 700, textAlign: 'right' }}>{formatMoney(jobCard.other_costs || 0)}</div>
              </div>
              <div style={{ marginTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <StatusPill label={getStatusLabel(jobCard.payment_status || jobCard.status)} background="#ecfeff" color="#155e75" />
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '12px' }}>
            <div style={sectionCard}>
              <SectionTitle>Diagnosis</SectionTitle>
              <div style={{ minHeight: '64px', whiteSpace: 'pre-wrap', lineHeight: 1.55, color: '#374151' }}>
                {jobCard.diagnosis || 'No diagnosis provided.'}
              </div>
            </div>

            <div style={sectionCard}>
              <SectionTitle>Work Performed</SectionTitle>
              <div style={{ minHeight: '92px', whiteSpace: 'pre-wrap', lineHeight: 1.55, color: '#374151' }}>
                {jobCard.work_performed || 'No work performed notes provided.'}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
            <div style={sectionCard}>
              <SectionTitle>Status & Invoice</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                <KeyValue label="Completed Date" value={formatDate(jobCard.completed_date)} />
                <KeyValue label="Invoiced Date" value={formatDate(jobCard.invoice_date || jobCard.invoiced_date)} />
                <KeyValue label="Invoice Reference" value={safeText(jobCard.invoice_reference)} />
                <KeyValue label="Invoice Week" value={safeText(jobCard.invoice_week)} />
                <KeyValue label="Payment Status" value={safeText(jobCard.payment_status)} />
                <KeyValue label="Paid By" value={safeText(jobCard.paid_by)} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '14px', borderTop: '1px solid #e5e7eb', paddingTop: '10px', fontSize: '10px', color: '#6b7280', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          <div>Printed by {printerName}</div>
          <div>Job Card No: {safeText(jobCard.job_card_no || jobCard.id)}</div>
        </div>
      </div>
    )
  });

  partsPages.forEach((pageRows, pageIndex) => {
    finalPages.push({
      key: `parts-${pageIndex}`,
      body: (
        <div style={pageShell}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #FF5733', paddingBottom: '12px', marginBottom: '12px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#2a2e30' }}>Parts Used</h2>
                <div style={{ marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                  Job Card #{safeText(jobCard.job_card_no || jobCard.id)} | Page {pageIndex + 1} of {partsPages.length + chargesPages.length + 1}
                </div>
              </div>
              <StatusPill label={`Rows ${pageIndex + 1}`} background="#eef2ff" color="#3730a3" />
            </div>

            <LineTable
              title="Parts Table"
              rows={pageRows}
              emptyText="No parts recorded."
              columns={[
                { key: 'part_name', label: 'Part', width: '30%', render: (row) => safeText(row.part_name || row.part_id) },
                { key: 'quantity_used', label: 'Qty', width: '9%', align: 'center' },
                { key: 'unit_cost', label: 'Unit Cost', width: '14%', align: 'right', render: (row) => formatMoney(row.unit_cost) },
                { key: 'discount_amount', label: 'Discount', width: '14%', align: 'right', render: (row) => formatMoney(row.discount_amount) },
                { key: 'total_cost', label: 'Total', width: '15%', align: 'right', render: (row) => formatMoney(row.total_cost) },
                { key: 'notes', label: 'Notes', width: '18%', render: (row) => safeText(row.notes) }
              ]}
              footer={
                pageIndex === partsPages.length - 1 ? (
                  <tr className="parts-footer">
                    <td colSpan={4} style={{ padding: '8px', border: '1px solid #d1d5db', textAlign: 'right', fontSize: '11px', fontWeight: 700 }}>
                      Parts Total
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #d1d5db', textAlign: 'right', fontSize: '11px', fontWeight: 800 }}>
                      {formatMoney(partsTotal)}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '11px' }} />
                  </tr>
                ) : null
              }
            />
          </div>

          <div style={{ marginTop: '14px', fontSize: '10px', color: '#6b7280', display: 'flex', justifyContent: 'space-between' }}>
            <div>Vehicle: {safeText(jobCard.plate_no)}</div>
            <div>Generated {formatDate(new Date())}</div>
          </div>
        </div>
      )
    });
  });

  chargesPages.forEach((pageRows, pageIndex) => {
    finalPages.push({
      key: `charges-${pageIndex}`,
      body: (
        <div style={pageShell}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #FF5733', paddingBottom: '12px', marginBottom: '12px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#2a2e30' }}>Charges</h2>
                <div style={{ marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                  Job Card #{safeText(jobCard.job_card_no || jobCard.id)} | Page {partsPages.length + pageIndex + 2} of {partsPages.length + chargesPages.length + 1}
                </div>
              </div>
              <StatusPill label={`Rows ${pageIndex + 1}`} background="#f3e8ff" color="#7c3aed" />
            </div>

            <LineTable
              title="Charge Table"
              rows={pageRows}
              emptyText="No charges recorded."
              columns={[
                { key: 'charge_type_name', label: 'Type', width: '20%', render: (row) => safeText(row.charge_type_name || row.charge_type_code) },
                { key: 'description', label: 'Description', width: '28%', render: (row) => safeText(row.description) },
                { key: 'amount', label: 'Amount', width: '12%', align: 'right', render: (row) => formatMoney(row.amount) },
                { key: 'discount_amount', label: 'Discount', width: '12%', align: 'right', render: (row) => formatMoney(row.discount_amount) },
                { key: 'total_amount', label: 'Total', width: '12%', align: 'right', render: (row) => formatMoney(row.total_amount) },
                { key: 'paid_by', label: 'Paid By', width: '16%', render: (row) => safeText(row.paid_by) }
              ]}
              footer={
                pageIndex === chargesPages.length - 1 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: '8px', border: '1px solid #d1d5db', textAlign: 'right', fontSize: '11px', fontWeight: 700 }}>
                      Charges Total
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #d1d5db', textAlign: 'right', fontSize: '11px', fontWeight: 800 }}>
                      {formatMoney(chargesTotal)}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '11px' }} />
                  </tr>
                ) : null
              }
            />
          </div>

          <div style={{ marginTop: '14px', fontSize: '10px', color: '#6b7280', display: 'flex', justifyContent: 'space-between' }}>
            <div>Invoice Reference: {safeText(jobCard.invoice_reference)}</div>
            <div>Generated {formatDate(new Date())}</div>
          </div>
        </div>
      )
    });
  });

  finalPages.push({
    key: 'summary',
    body: (
      <div style={pageShell}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', borderBottom: '2px solid #FF5733', paddingBottom: '12px', marginBottom: '12px' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#2a2e30' }}>Final Summary</h2>
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                Job Card #{safeText(jobCard.job_card_no || jobCard.id)} | Owner {safeText(jobCard.owner_name || jobCard.owner_code)}
              </div>
            </div>
            <StatusPill label={`Total ${formatMoney(totalCost)}`} background="#dcfce7" color="#166534" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '12px' }}>
            <div style={sectionCard}>
              <SectionTitle>Signoff</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                <KeyValue label="Mechanic" value={safeText(jobCard.service_location === 'in_house' ? jobCard.mechanic_name : jobCard.external_mechanic_name)} />
                <KeyValue label="Completed Date" value={formatDate(jobCard.completed_date)} />
                <KeyValue label="Closed Date" value={formatDate(jobCard.closed_date)} />
                <KeyValue label="Printed By" value={printerName} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '14px', borderTop: '1px solid #e5e7eb', paddingTop: '10px', fontSize: '10px', color: '#6b7280', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          <div>This document is system generated for operational and audit use.</div>
          <div>Generated on {formatDate(new Date())}</div>
        </div>
      </div>
    )
  });

  return (
    <div id="job-card-print-sheet" style={{ width: '210mm', boxSizing: 'border-box', margin: '0 auto', backgroundColor: '#fff', color: '#111827', position: 'relative' }}>
      {finalPages.map((page, index) => (
        <div
          key={page.key}
          className="job-card-pdf-page"
          style={{
            ...pageShell,
            pageBreakAfter: index === finalPages.length - 1 ? 'auto' : 'always',
            breakAfter: index === finalPages.length - 1 ? 'auto' : 'page'
          }}
        >
          {page.body}
        </div>
      ))}
    </div>
  );
};

export default JobCardPrintSheet;
